import { useState } from "react";
import { Loader2, ShieldCheck, HelpCircle, AlertTriangle, Wand2, Check, X, Undo2, ArrowRight, History, ChevronDown, ChevronRight } from "lucide-react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";

const STATUS = {
  confirmed: { label: "Confirmed contradiction", cls: "text-rose-200 bg-rose-950/50 border-rose-800/50", Icon: AlertTriangle },
  needs_review: { label: "Needs review", cls: "text-amber-200 bg-amber-950/40 border-amber-800/50", Icon: HelpCircle },
  insufficient: { label: "Not enough information", cls: "text-slate-300 bg-slate-800/50 border-slate-700/60", Icon: HelpCircle },
};

const Passage = ({ label, p, testid }) => (
  <div className="rounded-lg bg-navy-950/60 border border-white/5 p-3" data-testid={testid}>
    <div className="font-ui text-[10px] uppercase tracking-widest text-slate-500 mb-1">{label} · Chapter {p.chapter} · char {p.start}</div>
    <p className="font-read text-sm text-slate-200 italic">“{p.quote}”</p>
  </div>
);

const Instances = ({ f, onGoToChapter }) => {
  const [open, setOpen] = useState(false);
  const list = f.instances || [];
  if (list.length < 2 && !(f.ambiguous || []).length) return null;
  return (
    <div className="mt-2" data-testid={`finding-instances-${f.id}`}>
      <button onClick={() => setOpen(!open)} data-testid={`finding-instances-toggle-${f.id}`}
        className="flex items-center gap-1 font-ui text-xs text-gold-300 hover:text-gold-200">
        {open ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}
        {list.length} affected passage{list.length === 1 ? "" : "s"}{(f.ambiguous || []).length ? ` · ${f.ambiguous.length} ambiguous (not counted)` : ""}
      </button>
      {open && (
        <ul className="mt-2 space-y-1.5 max-h-80 overflow-y-auto pr-1">
          {list.map((i, n) => (
            <li key={`${i.chapter}-${i.start}`} className="font-read text-xs text-slate-300 flex gap-2" data-testid={`finding-instance-${f.id}-${n}`}>
              <button onClick={() => onGoToChapter(i.chapter)} className="shrink-0 text-gold-300 hover:text-gold-200 font-ui">Ch {i.chapter} · {i.start}</button>
              <span className="italic">“{i.quote}”</span>{i.value && <span className="shrink-0 text-slate-500">{i.value}</span>}
            </li>
          ))}
          {(f.ambiguous || []).map((a, n) => (
            <li key={`amb-${n}`} className="font-read text-xs text-slate-500 flex gap-2"><span className="shrink-0 font-ui">Ch {a.chapter} · unclear</span><span className="italic">“{a.quote}”</span></li>
          ))}
        </ul>
      )}
    </div>
  );
};

const Verdicts = ({ f }) => {
  if (!f.verdicts) return f.verifier_reason ? <p className="font-read text-xs text-slate-400 mt-2"><span className="text-slate-500">Independent check:</span> {f.verifier_reason}</p> : null;
  return (
    <div className="mt-2 grid md:grid-cols-2 gap-2" data-testid={`finding-verdicts-${f.id}`}>
      {Object.entries(f.verdicts).map(([k, v]) => (
        <p key={k} className="font-read text-xs text-slate-400" data-testid={`finding-verdict-${k}-${f.id}`}>
          <span className="font-ui text-slate-500">{k === "claude" ? "Claude" : "GPT"} ({v.model}): </span>
          <span className="text-slate-200">{v.status.replace("_", " ")} · {Math.round(v.confidence * 100)}%</span> {v.reason}
        </p>
      ))}
    </div>
  );
};

const Proposal = ({ f, onApply, onReject, busy }) => {
  const p = f.proposal;
  return (
    <div className="mt-3 rounded-lg border border-indigo-700/40 bg-indigo-950/30 p-3 space-y-2" data-testid={`continuity-proposal-${f.id}`}>
      <div className="grid md:grid-cols-[1fr_auto_1fr] gap-2 items-start">
        <p className="font-read text-sm text-rose-200/90 line-through decoration-rose-400/40" data-testid={`proposal-original-${f.id}`}>{p.original}</p>
        <ArrowRight className="w-4 h-4 text-slate-500 mt-1 hidden md:block" />
        <p className="font-read text-sm text-emerald-200" data-testid={`proposal-replacement-${f.id}`}>{p.replacement}</p>
      </div>
      <p className={`font-ui text-xs ${p.validated ? "text-emerald-300" : "text-rose-300"}`} data-testid={`proposal-validation-${f.id}`}>
        {p.validated ? "Validated against the full story record: resolves the issue, no new contradiction." : "Failed validation: "}
        {!p.validated && p.validation_reason}
      </p>
      {p.state === "proposed" && (
        <div className="flex gap-2">
          <button data-testid={`continuity-apply-${f.id}`} disabled={busy || !p.validated} onClick={onApply}
            className="flex items-center gap-1.5 font-ui text-xs font-medium bg-emerald-600 hover:bg-emerald-500 disabled:opacity-40 text-white rounded-lg px-3 py-1.5 transition-colors">
            <Check className="w-3.5 h-3.5" /> Apply this change
          </button>
          <button data-testid={`continuity-reject-${f.id}`} disabled={busy} onClick={onReject}
            className="flex items-center gap-1.5 font-ui text-xs font-medium text-slate-200 border border-white/10 hover:border-rose-400 rounded-lg px-3 py-1.5 transition-colors">
            <X className="w-3.5 h-3.5" /> Reject
          </button>
        </div>
      )}
      {p.state !== "proposed" && <p className="font-ui text-xs text-slate-400">Proposal {p.state}.</p>}
    </div>
  );
};

const FindingCard = ({ f, book, onGoToChapter, onBook, busy, setBusy }) => {
  const s = STATUS[f.status] || STATUS.insufficient;
  const call = async (fn, ok) => {
    setBusy(f.id);
    try { const r = await fn(); if (r?.chapters) onBook(r); else if (r?.finding) onBook({ ...book, continuity_report: { ...book.continuity_report, findings: book.continuity_report.findings.map((x) => (x.id === f.id ? r.finding : x)) } }); if (ok) toast.success(ok); }
    catch (e) { toast.error(apiError(e)); }
    finally { setBusy(null); }
  };
  return (
    <div data-testid={`continuity-finding-${f.id}`} className="rounded-xl border border-gold-500/10 bg-navy-900/50 p-4">
      <div className="flex flex-wrap items-center gap-2 mb-2">
        <span className={`inline-flex items-center gap-1 font-ui text-[11px] font-semibold border rounded-full px-2 py-0.5 ${s.cls}`} data-testid={`finding-status-${f.id}`}><s.Icon className="w-3 h-3" />{s.label}</span>
        <span className={`font-ui text-[11px] rounded-full px-2 py-0.5 border ${f.severity === "major" ? "border-rose-700/50 text-rose-200" : "border-slate-600 text-slate-300"}`} data-testid={`finding-severity-${f.id}`}>{f.severity} severity</span>
        <span className="font-ui text-[11px] text-slate-400" data-testid={`finding-confidence-${f.id}`}>~{Math.round(f.confidence * 100)}% confidence (estimate)</span>
        <span className="font-ui text-[11px] text-indigo-300">{f.category}</span>
        {f.state === "fixed" && <span className="font-ui text-[11px] text-emerald-300">Fixed</span>}
        <button onClick={() => onGoToChapter(f.chapter)} data-testid={`finding-goto-${f.id}`} className="ml-auto font-ui text-xs text-gold-300 hover:text-gold-200">Chapter {f.chapter}: {f.chapter_title} →</button>
      </div>
      <p className="font-read text-sm text-slate-200 mb-2">{f.detail}</p>
      <div className="grid md:grid-cols-2 gap-2">
        <Passage label="Established earlier" p={f.prior} testid={`finding-prior-${f.id}`} />
        <Passage label="Conflicting passage" p={f.current} testid={`finding-current-${f.id}`} />
      </div>
      {f.baseline_value && f.conflicting_value && (
        <p className="font-ui text-xs text-slate-400 mt-2" data-testid={`finding-values-${f.id}`}>{f.entity} · {f.attribute}: established <span className="text-emerald-300">{f.baseline_value}</span>, conflicting <span className="text-rose-300">{f.conflicting_value}</span></p>
      )}
      <Instances f={f} onGoToChapter={onGoToChapter} />
      <Verdicts f={f} />
      {f.recommendation && <p className="font-read text-xs text-slate-400 mt-1"><span className="text-slate-500">Recommended correction:</span> {f.recommendation}</p>}
      {f.fix_eligible && f.state !== "fixed" && !(f.proposal && f.proposal.state === "proposed") && (
        <button data-testid={`continuity-propose-${f.id}`} disabled={!!busy} onClick={() => call(() => api.continuityPropose(book.id, f.id))}
          className="mt-3 flex items-center gap-1.5 font-ui text-xs font-medium bg-indigo-600 hover:bg-indigo-500 disabled:opacity-40 text-white rounded-lg px-3 py-1.5 transition-colors">
          {busy === f.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Wand2 className="w-3.5 h-3.5" />} Propose fix
        </button>
      )}
      {!f.fix_eligible && <p className="font-ui text-[11px] text-slate-500 mt-2">{(f.group_size || 1) > 1 ? "No automatic fix yet: this affects several passages. Each one will need its own context-aware proposal and approval." : "No automatic fix: only findings both AI checks Confirm with 85%+ confidence can be fixed automatically. Review it yourself in the chapter."}</p>}
      {f.proposal && <Proposal f={f} busy={!!busy}
        onApply={() => call(() => api.continuityApply(book.id, f.id), "Change applied: only that passage was edited")}
        onReject={() => call(() => api.continuityReject(book.id, f.id), "Proposal rejected")} />}
    </div>
  );
};

const FixHistory = ({ book, onBook }) => {
  const [undoing, setUndoing] = useState(null);
  const fixes = book.continuity_fixes || [];
  if (!fixes.length) return null;
  const undo = async (id) => {
    setUndoing(id);
    try { onBook(await api.continuityUndo(book.id, id)); toast.success("Restored the exact original passage"); }
    catch (e) { toast.error(apiError(e)); }
    finally { setUndoing(null); }
  };
  return (
    <div className="mt-4 rounded-xl border border-white/5 p-4" data-testid="continuity-fix-history">
      <h4 className="font-ui text-xs font-semibold text-slate-300 mb-2 flex items-center gap-1.5"><History className="w-3.5 h-3.5" /> Applied corrections</h4>
      <ul className="space-y-2">
        {fixes.map((x) => (
          <li key={x.id} className="font-read text-xs text-slate-400 flex items-start gap-2" data-testid={`fix-history-${x.id}`}>
            <span className="shrink-0 text-slate-500">Ch {x.chapter}</span>
            <span className="flex-1"><span className="line-through">{x.original}</span> → <span className="text-slate-200">{x.replacement}</span></span>
            {x.undone ? <span className="text-slate-500">undone</span> : (
              <button data-testid={`continuity-undo-${x.id}`} onClick={() => undo(x.id)} disabled={!!undoing}
                className="shrink-0 flex items-center gap-1 text-gold-300 hover:text-gold-200">
                {undoing === x.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <Undo2 className="w-3 h-3" />} Undo
              </button>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
};

export const ContinuityReportV2 = ({ book, onGoToChapter, onBook }) => {
  const [busy, setBusy] = useState(null);
  const [proposingAll, setProposingAll] = useState(false);
  const rep = book.continuity_report;
  const eligible = rep.findings.filter((f) => f.fix_eligible && f.state !== "fixed" && !f.proposal);
  const prev = book.continuity_history?.[0];
  const proposeAll = async () => {
    setProposingAll(true);
    let cur = book;
    for (const f of eligible) {
      try {
        const r = await api.continuityPropose(book.id, f.id);
        cur = { ...cur, continuity_report: { ...cur.continuity_report, findings: cur.continuity_report.findings.map((x) => (x.id === f.id ? r.finding : x)) } };
        onBook(cur);
      } catch (e) { toast.error(`Chapter ${f.chapter}: ${apiError(e)}`); }
    }
    setProposingAll(false);
  };
  return (
    <div className="mt-4 space-y-3" data-testid="continuity-report-v2">
      <div className="flex flex-wrap items-center gap-3 font-ui text-xs text-slate-300">
        <ShieldCheck className="w-4 h-4 text-emerald-300" />
        <span data-testid="continuity-counts">{rep.counts.confirmed} confirmed · {rep.counts.needs_review} need review · {rep.counts.insufficient} not enough info</span>
        {prev && <span className="text-slate-500" data-testid="continuity-previous">Previous report: {prev.flagged} chapter(s) flagged{prev.flags ? ` (${prev.flags.filter((x) => x.severity === "major").length} major)` : ""}</span>}
        {eligible.length > 0 && (
          <button data-testid="continuity-propose-all" disabled={proposingAll || !!busy} onClick={proposeAll}
            className="ml-auto flex items-center gap-1.5 font-ui text-xs font-medium bg-indigo-600 hover:bg-indigo-500 disabled:opacity-40 text-white rounded-lg px-3 py-1.5 transition-colors">
            {proposingAll ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Wand2 className="w-3.5 h-3.5" />} Propose fixes for all Confirmed ({eligible.length})
          </button>
        )}
      </div>
      {rep.findings.length === 0 && <div data-testid="continuity-clean" className="font-read text-sm text-emerald-300 bg-emerald-950/30 border border-emerald-800/40 rounded-xl px-4 py-3">No contradictions found.</div>}
      {rep.findings.map((f) => <FindingCard key={f.id} f={f} book={book} onGoToChapter={onGoToChapter} onBook={onBook} busy={busy} setBusy={setBusy} />)}
      <FixHistory book={book} onBook={onBook} />
    </div>
  );
};
